import type { ExportFormat } from "~/features/analysisReport/analysisReport.types";
export const appRoutes = {
  home: () => "/",
  homeProductionApi: () => "/home/production/api",
  homeFavoritesApi: () => "/home/favorites/api",
  /** US 28798: the navigation's "Choose" picker for one Analytics dimension page (GET list, POST open). */
  navigationChoose: (pageKey: string) => `/resources/navigation/choose/${encodeURIComponent(pageKey)}`,
  branch: {
    index: () => "/branch",
    branchApi: () => "/branch/api",
  },
  guest: {
    index: () => "/guest",
  },
  loginFailure: () => "/login-failure",
  accessDenied: () => "/access-denied",
  admin: {
    index: () => "/admin",
    /** US 28792: oraHome › Tiles, each leader persona's For you tiles and default favorites. */
    tiles: () => "/admin/orahome/tiles",
    /** US 28878: oraHome › Navigation Bar, the navigation's wording, order, visibility and images. */
    navigationBar: () => "/admin/orahome/navigation-bar",
    section: (group: string, section: string) => `/admin/${group}/${section}`,
    /** Moved from the Workflows menu into the admin console (2026-10-06). */
    spPool: () => "/admin/workflows/sp-pool",
    monitoredMailboxes: () => "/admin/workflows/monitored-mailboxes",
    /** US 28994: ora Workflows > Branch Settings (hours, reminder timing, volume cutoff, headlines per branch). */
    branchSettings: () => "/admin/workflows/branch-settings",
    monitoredMailboxPeople: (mailbox: string) =>
      `/admin/workflows/monitored-mailboxes/people?mailbox=${encodeURIComponent(mailbox)}`,
    /** s9: oraChat › Surface editor (the grid), one surface's page, and its prompt editors. */
    surfaces: () => "/admin/orachat/surfaces",
    surface: (slug: string) => `/admin/orachat/surfaces/${encodeURIComponent(slug)}`,
    surfacePrompt: (slug: string) =>
      `/admin/orachat/surfaces/${encodeURIComponent(slug)}/prompt`,
    packPrompt: (slug: string, pack: string) =>
      `/admin/orachat/surfaces/${encodeURIComponent(slug)}/packs/${encodeURIComponent(pack)}/prompt`,
    /** s9: oraChat › Skills & Tools, with oraChat docked beside it. */
    skillsTools: () => "/admin/orachat/skills-tools",
    /** s9 lists them (Admin or Workflow-Author); s12 / s18 build the pages. */
    workbench: () => "/admin/workflows/workbench",
    agents: () => "/admin/workflows/agents",
    /** s12: one document-analysis skill's page in the Workbench (its Overview), keyed by the skill's kind. */
    workbenchSkill: (kind: string) => `/admin/workflows/workbench/${encodeURIComponent(kind)}`,
    /** s12: one tab or step of a skill's page (feedback, refine, test, publish, versions, settings). */
    workbenchSkillTab: (kind: string, tab: string) =>
      `/admin/workflows/workbench/${encodeURIComponent(kind)}/${tab}`,
    /** s28: Refine Workflow's steps — Refine (the skill itself), Publish and Settings. */
    workbenchRefine: (kind: string) => `/admin/workflows/workbench/${encodeURIComponent(kind)}/refine`,
    workbenchPublish: (kind: string) => `/admin/workflows/workbench/${encodeURIComponent(kind)}/publish`,
    workbenchSettings: (kind: string) => `/admin/workflows/workbench/${encodeURIComponent(kind)}/settings`,
    /** s12 (AD4): Registrations' form, opened with a new tile filled in from the skill (Admin only). */
    workbenchOffer: (kind: string) => `/admin/workflows/workbench/${encodeURIComponent(kind)}/offer`,
    /** s14: one test of a skill (progress, then its Review; s28: the side-by-side is part of the Review). */
    workbenchTest: (kind: string, testId: string) =>
      `/admin/workflows/workbench/${encodeURIComponent(kind)}/test/${encodeURIComponent(testId)}`,
  },
  maintenance: () => "/maintenance",
  authentication: {
    login: () => "/auth/microsoft",
    logout: () => "/auth/logout",
  },
  leaderboards: {
    goBeyond: () => "/leaderboards/go-beyond",
  },
  insights: {
    index: () => "/insights",
    // Benchmarks — dedicated Benchmark / Benchmark details / Compare page.
    // Local stakeholder prototype; see
    // docs/superpowers/plans/2026-08-06-benchmarks-page-prototype.md
    benchmarks: () => "/insights/benchmarks",
    benchmarksDistributionApi: () => "/insights/benchmarks/distributionapi",
    benchmarksCompareApi: () => "/insights/benchmarks/compareapi",
    commissionDrivers: () => "/insights/revenue-drivers-analysis",
    commissionDriversApi: () => "/insights/revenue-drivers-analysis/api",
    // US27925: Carrier Relationship Analysis — RDA transposed (carrier as the lens).
    carrierRelationshipAnalysis: () => "/insights/carrier-relationship-analysis",
    carrierRelationshipAnalysisApi: () =>
      "/insights/carrier-relationship-analysis/api",
    // US27927/27928: the grain-dependent legs (Growth by geo + Revenue Matrix by geo) — split
    // from the bridge leg so a View by Region | Branch change never refetches the bridges.
    carrierRelationshipAnalysisGeoApi: () =>
      "/insights/carrier-relationship-analysis/geoapi",
    // Production Insights (Databricks-backed) at /insights/production.
    production: () => "/insights/production",
    productionApi: () => "/insights/production/api",
    productionBusinessInsightsApi: () =>
      "/insights/production/businessinsightsapi",
    productionKpiTrendsApi: () => `/insights/production/kpitrendsapi`,
    productionMixOfBusinessApi: () =>
      `/insights/production/mixofbusinessapi`,
    productionEmployeesApi: () => `/insights/production/employeesapi`,
    productionRegionMapApi: () => `/insights/production/regionmapapi`,
    // Bug 26030 finding 2: the map widget's own data feed (off the deferred slicer stream).
    productionRegionMapDetailsApi: () => `/insights/production/regionmapdetailsapi`,
    oraPivot: (from?: string) =>
      from ? `/insights/ora-pivot?from=${from}` : "/insights/ora-pivot",
    oraPivotApi: () => "/insights/ora-pivot/api",
    cams: () => "/insights/cams",
    camsApi: () => "/insights/cams/api",
    topClients: () => "/insights/top-clients",
    topClientsApi: () => "/insights/top-clients/api",
    topClientsRetentionApi: () => "/insights/top-clients/retention/api",
    topLostClients: () => "/insights/top-lost-clients",
    topLostClientsApi: () => "/insights/top-lost-clients/api",
    // US28221: Production Metrics (flag: production-metrics) and its resource route.
    productionMetrics: () => "/insights/production-metrics",
    productionMetricsApi: () => "/insights/production-metrics/api",
    // oraMaps (flag: community-corner-map). Moved under Insights from
    // /expertises/community-corner-map during the Insights menu add.
    oraMaps: () => "/insights/ora-maps",
    oraMapsApi: () => "/insights/ora-maps/api",
    // oraMaps-owned roster route (Databricks-only: /Employees/AllV2/dbx).
    oraMapsEmployeesApi: () => "/insights/ora-maps/employeesapi",
    // oraMaps-owned per-person expertise route (kpi.community_corner_producer_expertise).
    oraMapsExpertiseApi: () => "/insights/ora-maps/expertiseapi",
    // oraMaps-owned per-office Mix of Business route — a POST ({ locationName })
    // reusing the Production Insights mix service (no new backend). Listed in the
    // page's shouldRevalidate skip-set (a completed POST must not re-init the map).
    oraMapsMixApi: () => "/insights/ora-maps/mixapi",
    // oraMaps-owned Opportunity route (Mix of Business by office) — a POST ({ grain,
    // category? }) returning the grain's category list + per-office totals, or a
    // category's per-office values. Behind the oraMaps page gate (community-corner-map); in the
    // skip-set too.
    oraMapsMobShare: () => "/insights/ora-maps/mob-share",
    // oraMaps-owned Client Density route — a GET serving pre-aggregated active-client
    // counts per ZIP (aggregate only; no size floor, so a one-client ZIP shows that client). Behind the oraMaps
    // page gate (community-corner-map); in the page's shouldRevalidate skip-set.
    oraMapsClientDensity: () => "/insights/ora-maps/client-density",
    oraMapsScopeSummary: () => "/insights/ora-maps/scope-summary",
    // The office card's reads (office list + pins), re-run by its Try again and by Reset.
    oraMapsOffices: () => "/insights/ora-maps/offices",
    // The office search (US 28742): its lists (GET, read when it first opens) and the search (POST).
    // Both are read with a plain fetch, so neither revalidates the page.
    oraMapsOfficeSearchOptions: () => "/insights/ora-maps/office-search-options",
    oraMapsOfficeSearch: () => "/insights/ora-maps/office-search",
    trendsLanding: () => "/insights/trends-landing",
    trendsv2: () => "/insights/trendsv2",
    trendsv2Api: () => "/insights/trendsv2/api",
    trendsv2Landing: () => "/insights/trendsv2-landing",
    trendsv2WithKpi: (kpiId: number) => `/insights/trendsv2?kpiId=${kpiId}`,
    trendsLaunch: () => "/insights/trends-launch",
    analyticsLaunch: () => "/insights/analytics-launch",
    reportsApi: () => "/insights/reports/api",
  },
  possibilities: {
    prospects: () => "/possibilities/prospects",
    prospectsApi: () => "/possibilities/prospects/api",
    prospectsRecordApi: () => "/possibilities/prospects/record/api",
    prospectsImportApi: () => "/possibilities/prospects/import/api",
    renewals: () => "/possibilities/renewals",
    renewalsApi: () => "/possibilities/renewals/api",
    renewalsGridApi: () => "/possibilities/renewals/renewalsgridapi",
  },
  strategies: {
    index: () => "/strategies",
    twoMoreFor24: () => "/strategies/2-more-for-24",
    twoMoreFor24Api: () => "/strategies/2-more-for-24/api",
  },
  expertises: {
    index: () => "/expertises",
    communityCorner: () => "/expertises/community-corner",
    communityCornerApi: () => "/expertises/community-corner/api",
    communityCornerReferralsApi: () =>
      "/expertises/community-corner/referralsapi",
  },
  resources: {
    index: () => "/resources",
    addAnIdea: () => "/resources/add-an-idea",
    chat: () => "/resources/chat",
    offices: () => "/resources/offices",
    chatApi: () => "/resources/chat/api",
    chatFeedbackApi: () => "/resources/chat/feedback/api",
    chatThreadsApi: () => "/resources/chat/threads/api",
    chatSuggestionsApi: () => "/resources/chat/suggestions/api",
    chatFileUploadApi: () => "/resources/chat/fileupload/api",
    chatDocumentsApi: () => "/resources/chat/documents/api",
    /** The Document Store from the browser (DS.P4): POST multipart `file` → .NET
     *  /api/Documents/upload, answering {document_id, filename, processing_status};
     *  GET `?ids=` → each document's processing_status. */
    documentsApi: () => "/resources/documents/api",
    /** oraVault v2: GET the caller's documents + reports; PATCH/PUT/DELETE `?id=`
     *  rename, file under a client / policy, delete (409 while a report uses it). */
    documentsVaultApi: (clientSk?: number) =>
      clientSk && clientSk > 0
        ? `/resources/documents/vault/api?clientSk=${clientSk}`
        : "/resources/documents/vault/api",
    /** The vault's Epic attachments for a client (GET `?clientSk=&policySk=`), "use"
     *  (POST: their shared copies) and "remove from my vault" (DELETE `?id=`). */
    documentsVaultEpicApi: () => "/resources/documents/vault/epic/api",
    /** One of a client's Epic emails opened (oravault-redesign s8, s4's route): GET → its headers, body and
     *  attachments, or `status: "unreadable"`. */
    documentsVaultEpicEmailApi: (clientSk: number, attachmentId: string) =>
      `/resources/documents/vault/epic/email/api?clientSk=${clientSk}&attachmentId=${encodeURIComponent(attachmentId)}`,
    /** The raw Epic email, streamed as a download (Download email). */
    documentsVaultEpicEmailDownload: (clientSk: number, attachmentId: string) =>
      `/resources/documents/vault/epic/email/download?clientSk=${clientSk}&attachmentId=${encodeURIComponent(attachmentId)}`,
    /** Use other brokers' uploads filed to a client (oravault-redesign s8): POST `{client_sk, document_ids}`. */
    documentsVaultSharedApi: () => "/resources/documents/vault/shared/api",
    /** The vault's one search box (oravault-redesign s6): GET `?q=&group=&limit=` answers ONE
     *  group — clients, prospects or policies — as {status, items, total}. */
    documentsVaultSearchApi: () => "/resources/documents/vault/search/api",
    /** The caller's oraVault push channel (`vault-user-<oid>`): document status events. */
    orachatVaultPushChannel: () => "/resources/orachat/vault/push-channel",
    /** The vault's policy pick: a client and its policies (Epic PolicyIDs). */
    documentsFilingPoliciesApi: (clientSk: number) =>
      `/resources/documents/filing/policies/api?clientSk=${clientSk}`,
    /** One of the caller's files; `inline` opens it in the browser. */
    documentContent: (documentId: string, inline = false, clientSk?: number) => {
      const query = [
        inline ? "inline=1" : "",
        clientSk && clientSk > 0 ? `clientSk=${clientSk}` : "",
      ].filter(Boolean);
      return `/resources/documents/${documentId}/content${query.length ? `?${query.join("&")}` : ""}`;
    },
    fileUploadMaxSizeApi: () => "/resources/file-upload-max-size/api",
    /** The document analyses this user may launch (their Workflows Hub tiles). */
    documentAnalysisTypesApi: () => "/resources/document-analysis/types/api",
    insightsApi: () => "/resources/insightsapi",
    workflowsApi: () => "/resources/workflows/api",
  },
  profiles: {
    impersonate: () => "/profiles/impersonate",
    myDashboard: () => "/profiles/my-dashboard",
    myActivity: () => "/profiles/my-activity",
    myActivityApi: () => "/profiles/my-activity/api",
    myTeamActivityApi: () => "/profiles/my-team-activity/api",
    myTeamActivity: () => "/profiles/my-team-activity",
    myActivitySalesReportApi: () => "/profiles/my-activity/salesreportapi",
    myActivityCloseReferralApi: () => "/profiles/my-activity/closereferralapi",
    myActivityValidationApi: () => "/profiles/my-activity/validationapi",
    myTeamActivityValidationApi: () =>
      "/profiles/my-team-activity/validationapi",
  },
  reports: {
    index: () => "/reports",
    byKey: (reportKey: string) => `/reports/${reportKey}`,
  },
  clients: {
    clientApi: () => "/clients/api",
  },
  googleMap: {
    placesApi: () => "/google/places/api",
  },
  employees: {
    employeeApi: () => "/employees/api",
  },
  seepossibilities: {
    feedbackApi: () => "/seepossibilities/feedback/api",
  },
  myDashboard: {
    myDashboardApi: () => "/mydashboard/api",
    tilesApi: () => "/mydashboard/tiles",
    columnsApi: () => "/mydashboard/columns",
  },
  workflows: {
    index: () => "/workflows",
    monitor: () => "/workflows/monitor",
    entraSync: () => "/workflows/entra-sync",
    entraSyncApi: () => "/workflows/entra-sync/api",
    entraSyncOutcomesApi: (instanceId: string) => `/workflows/entra-sync/${encodeURIComponent(instanceId)}/outcomes/api`,
    entraSyncRevertApi: (instanceId: string) => `/workflows/entra-sync/${encodeURIComponent(instanceId)}/revert/api`,
    entraSyncScheduleApi: () => "/workflows/entra-sync/schedule/api",
    profiseeMatchJudge: () => "/workflows/profisee-match-judge",
    /**
     * The Monitor's own one-shot `?instance=` deep-link handler (see
     * workflows.monitor.tsx): resolves a run that is NOT in the current grid
     * page by fetching it by id, then opens that row's drawer directly — the
     * one place a HITL question is actually answered (`DecisionCard` +
     * `useHitlSubmitLatch`). Its live caller today is the run-record page's
     * "needs you" banner (`workflows.runs.$instanceId.tsx`'s
     * `RunNeedsYouBanner actionHref`), so a reader who arrived at a run from
     * a bookmark rather than the grid still lands on the right drawer.
     */
    monitorInstance: (instanceId: string) =>
      `/workflows/monitor?instance=${encodeURIComponent(instanceId)}`,
    /**
     * Both params are REQUIRED on purpose (US 25879).
     *
     * They were optional, and omitting `scope` silently dropped it from the
     * query string — the API then received null and the substrate fell back to
     * `self`. The poll loop did exactly that, so the grid collapsed to the
     * user's own work seconds after they selected Branch or Region, while the
     * View toggle still read "Branch workflows". Requiring the argument turns
     * that mistake into a compile error instead of a silent narrowing. Pass an
     * explicit `"self"` if that is genuinely what you want.
     *
     * `drill` is optional: no drill means the viewer's whole scope.
     */
    monitorApi: (window: string, scope: string, drill?: { region?: string; branch?: string }) => {
      const params = new URLSearchParams();
      if (window) params.set("window", window);
      // Requested VIEW (self/branch/region/all) — a server-side filter, unlike
      // `window` which the client applies. Clamped to the caller's ceiling.
      if (scope) params.set("scope", scope);
      // Leader governance phase 3 (ruling 9): the drill narrows the roll-up.
      // The API bounds it to the caller's ceiling, so it can only narrow.
      if (drill?.region) params.set("region", drill.region);
      if (drill?.branch) params.set("branch", drill.branch);
      const qs = params.toString();
      return qs ? `/workflows/monitor/api?${qs}` : "/workflows/monitor/api";
    },
    /** Phase 3 (26764): the itemised rows behind one triage count. `mine` is sent only when true. */
    monitorTriageItemsApi: (params: { bucket: string; mailbox?: string; branch?: string; region?: string; mine?: boolean }) => {
      const query = new URLSearchParams({ bucket: params.bucket });
      if (params.mailbox) query.set("mailbox", params.mailbox);
      if (params.branch) query.set("branch", params.branch);
      if (params.region) query.set("region", params.region);
      if (params.mine) query.set("mine", "true");
      return `/workflows/monitor/triage-items?${query.toString()}`;
    },
    /** Phase 3 (D9): POST target of the Runs table's Nudge confirm. */
    monitorNudgeApi: () => "/workflows/monitor/nudge",
    /** The client Workflows drawer's list (P2): `query` names the client or prospect. */
    clientRunsApi: (query: string) => `/workflows/client-runs/api?${query}`,
    launch: () => "/workflows/launch",
    /** US 28430 (D22): an Outmarket tile's link, built by the API from its provider's route template. */
    outmarket: (module: string, workflow: string) =>
      `/workflows/outmarket/${encodeURIComponent(module)}/${encodeURIComponent(workflow)}`,
    /** US 28636: POST target that records a started Outmarket run with the substrate. */
    outmarketRunsApi: () => "/api/workflows/outmarket/runs",
    review: (instanceId: string) => `/workflows/review/${instanceId}`,
    /**
     * The run-record page (26742) — a durable, shareable URL for one run's
     * full decision/evidence/result history, distinct from the drawer (which
     * is scoped to the Monitor grid session). `encodeURIComponent` because an
     * instance id can end up in this string from a notification link or a
     * copy/paste, not only from a trusted grid row.
     */
    runRecord: (instanceId: string) =>
      `/workflows/runs/${encodeURIComponent(instanceId)}?record=1`,
    /**
     * Story 29001 — the run detail page (the one the Outlook pane renders), on ora. Distinct from
     * `runRecord`: no `?record=1`, and the file is `workflows.runs.$instanceId_.detail.tsx`.
     */
    runDetail: (instanceId: string) =>
      `/workflows/runs/${encodeURIComponent(instanceId)}/detail`,
    /**
     * Story 26752 (ora surface) — a triaged-but-not-yet-launched brief,
     * addressable before a run exists (26751's gap: today only the pane can
     * open one). `messageId` is the DECLARED RFC 5322 Message-ID; `mailbox`
     * narrows which mailbox's triage record to read (story 26727's row key),
     * omitted resolves to "any mailbox for this identity" server-side, same
     * as `getEmailRunState`'s own default.
     */
    triage: (messageId: string, mailbox?: string | null) => {
      const path = `/workflows/triage/${encodeURIComponent(messageId)}`;
      return mailbox ? `${path}?mailbox=${encodeURIComponent(mailbox)}` : path;
    },
    /**
     * Where a carrier-packet "copy" link lands (26732). The value rides in the
     * URL FRAGMENT, which is never sent to a server — see the route's header —
     * so this entry deliberately takes no arguments. The substrate builds the
     * full link in `notifications/composer.py::copy_link`; this is here so the
     * path exists in one place on the client side too.
     */
    copy: () => "/workflows/copy",
    instanceDetail: (id: string) => `/workflows/instances/${id}`,
    instancesApi: () => "/workflows/instances/api",
    typesApi: () => "/workflows/types/api",
    catalogApi: () => "/workflows/catalog/api",
    quoteToPolicy: { upload: () => "/workflows/quote-to-policy/upload" },
    proposals: { upload: () => "/workflows/proposals/upload" },
    contractReview: { upload: () => "/workflows/contract-review/upload" },
    orafix: {
      launch: () => "/workflows/orafix/launch",
      startApi: () => "/workflows/orafix/start/api",
      censusApi: () => "/workflows/orafix/census/api",
      impactApi: () => "/workflows/orafix/impact/api",
      instance: (instanceId: string) => `/workflows/orafix/${encodeURIComponent(instanceId)}`,
      outcomesApi: (instanceId: string) =>
        `/workflows/orafix/${encodeURIComponent(instanceId)}/outcomes/api`,
      revertApi: (instanceId: string) =>
        `/workflows/orafix/${encodeURIComponent(instanceId)}/revert/api`,
      campaignLaunch: () => "/workflows/orafix/campaign/launch",
      campaignStartApi: () => "/workflows/orafix/campaign/start/api",
      campaign: (instanceId: string) =>
        `/workflows/orafix/campaign/${encodeURIComponent(instanceId)}`,
      campaignStopApi: (instanceId: string) =>
        `/workflows/orafix/campaign/${encodeURIComponent(instanceId)}/stop/api`,
      campaignCancelApi: (instanceId: string) =>
        `/workflows/orafix/campaign/${encodeURIComponent(instanceId)}/cancel/api`,
    },
    mortgageeUpdate: { upload: () => "/workflows/mortgagee-update/upload" },
    secondHomeIdentification: {
      upload: () => "/workflows/second-home-identification/upload",
      startApi: () => "/workflows/second-home-identification/start/api",
    },
    homeownerUpsell: { upload: () => "/workflows/homeowner-upsell/upload" },
    lapsedClientWinback: { upload: () => "/workflows/lapsed-client-winback/upload" },
    /** The count behind the "N waiting on you" indicator. No parameters. */
    waitingOnMeApi: () => "/workflows/waiting-on-me/api",
    /** USER-scoped SignalR negotiate (`user-<oid>`). No instance id. */
    userPushChannelApi: () => "/workflows/push-channel/api",

  },
  /** Story 28997: the v4 pane's pages on ora; `launch` renders the shared Launch home. */
  pane: {
    launch: () => "/pane/launch",
  },
  testing: {
    test: () => "/testing",
    test2: () => "/testing/2",
    miniMap: () => "/testing/mini-map",
  },
  // The "api" segment keeps these out of the oraChat live route manifest.
  miniMap: {
    api: () => "/mini-map/api",
    insightsApi: () => "/mini-map/insights/api",
    topMarketsApi: (measure: string, level: string) =>
      `/mini-map/top-markets/api?measure=${encodeURIComponent(measure)}&level=${encodeURIComponent(level)}`,
  },
  user: {
    actionLogApi: () => "/user/actionlog/api",
    avatarApi: () => "/user/avatar/api",
    pageSettingsApi: () => "/user/page-settings/api",
  },
  auditlogs: {
    api: () => "/auditlogs/api",
  },
  policyCheck: {
    upload: () => "/policy-check/upload",
    review: (instanceId: string) => `/policy-check/${instanceId}`,
    findingsApi: (instanceId: string) =>
      `/policy-check/${instanceId}/findings/api`,
    triageApi: (instanceId: string, findingId: string) =>
      `/policy-check/${instanceId}/findings/${findingId}/triage/api`,
    commentApi: (instanceId: string, findingId: string) =>
      `/policy-check/${instanceId}/findings/${findingId}/comment/api`,
    negotiateApi: (instanceId: string) =>
      `/policy-check/${instanceId}/negotiate/api`,
  },
  /** The legacy quote_compare_v1 launcher (its runs open on the document-analysis page). */
  quoteCompare: {
    upload: () => "/quote-compare/upload",
  },
  documentAnalysis: {
    /**
     * A run's report page (G2, D3.25): one page for every analysis type. `kind` is
     * the analysis type (a skill's kind; a legacy quote comparison is
     * "quote-compare"); the page sends a run opened under another kind to its own.
     * The per-type path is what the chat's page skills match on.
     */
    review: (kind: string, instanceId: string) =>
      `/document-analysis/${encodeURIComponent(kind)}/${instanceId}`,
    /** The upload page that starts an analysis of `kind`. */
    launch: (kind: string) => `/document-analysis/${encodeURIComponent(kind)}/new`,
    findingsApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/findings/api`,
    documentApi: (instanceId: string, documentId: string) =>
      `/document-analysis/${instanceId}/document/api?document_id=${encodeURIComponent(documentId)}`,
    /** One page of a source file and the text the extractor read off it (the Sources viewer's Text view). */
    documentPageApi: (instanceId: string, documentId: string, pageNumber: number) =>
      `/document-analysis/${instanceId}/document-page/api?document_id=${encodeURIComponent(documentId)}&page=${pageNumber}`,
    printPage: (kind: string, instanceId: string) =>
      `/document-analysis/${encodeURIComponent(kind)}/${instanceId}/print`,
    reportDocumentApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/report-document/api`,
    /** The per-user unsaved-edit tier. GET restores it, PUT writes it, DELETE
     *  discards it. Typing goes here; only Save goes to reportDocumentApi. */
    reportDraftApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/report-document/draft/api`,
    reportRevertApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/report-document/revert/api`,
    /** W13: PUT {on, statement?} — the draft watermark and its sign-off. */
    reportWatermarkApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/report-document/watermark/api`,
    reportRevisionsApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/report-document/revisions/api`,
    reportRevisionApi: (instanceId: string, revision: number) =>
      `/document-analysis/${instanceId}/report-document/revisions/${revision}/api`,
    epicAttachApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/epic-attach/api`,
    /** File > Assign to Client: POST {client_sk} links the run to a client. */
    assignClientApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/client/api`,
    /** Server-minted conversation for this analysis (per user). The chat pane
     *  resolves it client-side after mount; nothing derives an id locally. */
    chatConversationApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/chat-conversation/api`,
    /** Page-owned oraChat turn proxies — they stamp `X-Ora-Surface:
     *  quote-compare` server-side, which the generic `/resources/orachat/*`
     *  routes deliberately do not. A run's resources are keyed by the run
     *  alone (`/document-analysis/<id>/…`); only its page carries the type. */
    orachatAskApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/orachat/ask/api`,
    orachatContinueApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/orachat/continue/api`,
    orachatAbortApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/orachat/abort/api`,
    orachatRealtimeSessionApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/orachat/voice/realtime/session/api`,
    orachatSttApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/orachat/voice/stt/api`,
    /** Extracted source-quote text for chat Q&A against the original PDFs. */
    sourceDocumentsApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/source-documents/api`,
    /** Add ONE more carrier quote to an open analysis. Multipart (`file`, `label`);
     *  the substrate refuses a sixth quote and an unreadable document itself. */
    reportExtendApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/report-document/extend/api`,
    /** Re-run the whole analysis. JSON `{confirm}`; without it the substrate answers
     *  409 with the edits-may-be-lost warning and changes nothing. */
    reportRerunApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/report-document/rerun/api`,
    /** End (ADR-002): save the last version, lock the report, keep the chat.
     *  JSON `{rating?: {score?, comment?}, draft?: {html, base_revision}}`. */
    reportFinalizeApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/report-document/finalize/api`,
    /** Reopen a finalized report for changes (owner only). */
    reportUnlockApi: (instanceId: string) =>
      `/document-analysis/${instanceId}/report-document/unlock/api`,
    /** The ONE place the export query is assembled. `coverPage` is appended
     *  only when the caller supplies it — the substrate owns the default, and
     *  Word (docx) is never given the parameter at all (AC6). */
    exportApi: (instanceId: string, format: ExportFormat, coverPage?: boolean) =>
      `/document-analysis/${instanceId}/export/api?format=${format}` +
      (coverPage === undefined ? "" : `&cover_page=${coverPage ? "true" : "false"}`),
  },
  epicPolicyUpdateBulk: {
    instance: (instanceId: string) =>
      `/workflows/epic-policy-update-bulk/${encodeURIComponent(instanceId)}`,
    referenceDocApi: (instanceId: string, proposalId: string, docIndex?: number) =>
      `/workflows/epic-policy-update-bulk/${encodeURIComponent(instanceId)}/reference-doc/api?proposal_id=${encodeURIComponent(proposalId)}` +
      (Number.isInteger(docIndex) ? `&doc_index=${docIndex}` : ""),
  },
  knowledgeGraph: {
    clientPoliciesApi: (clientSk: number, lookupCode?: string) =>
      lookupCode
        ? `/api/workflows/clients/${clientSk}/policies-with-attachments?lookupCode=${encodeURIComponent(lookupCode)}`
        : `/api/workflows/clients/${clientSk}/policies-with-attachments`,
  },
};

/** Production Insights page path — always the Databricks page (/insights/production). */
export const productionInsightsPath = (): string =>
  appRoutes.insights.production();
